import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useAuthStore } from '../common/store/auth.store'
import { request } from 'src/utils/requestV2'

const STORAGE_KEY = 'cf_app_configuration'
const REFRESH_INTERVAL_MS = 60 * 60 * 1000

interface AppConfigurationContext {
  isAuthenticated: boolean
  orgId: string
  centerId: string
}

interface AppConfigurationEntry {
  configuration: Record<string, unknown>
  resolvedAt: string
}

interface AppConfigurationByCenter {
  [centerId: string]: AppConfigurationEntry
}

interface AppConfigurationStorage {
  [orgId: string]: AppConfigurationByCenter
}

interface InitializeOptions {
  blockWhenMissing?: boolean
}

export const useAppConfigurationStore = defineStore('appConfiguration', () => {
  const configurations = ref<AppConfigurationStorage>({})
  const currentConfiguration = ref<Record<string, unknown>>({})
  const currentContext = ref<AppConfigurationContext>()
  const isInitialized = ref(false)
  const isLoading = ref(false)
  const refreshInterval = ref<ReturnType<typeof setInterval>>()
  const pendingLoads = new Map<string, Promise<Record<string, unknown>>>()

  const authStore = useAuthStore()

  const initialize = async (options: InitializeOptions = {}) => {
    if (!authStore.isAuthenticated || !authStore.currentOrgId) return
    if (!authStore.currentCenterId) return

    const context: AppConfigurationContext = {
      isAuthenticated: authStore.isAuthenticated,
      orgId: authStore.currentOrgId,
      centerId: authStore.currentCenterId,
    }

    loadFromStorage()
    currentContext.value = context

    const storedEntry = getStoredEntry(context)
    if (storedEntry) {
      currentConfiguration.value = storedEntry.configuration
      isInitialized.value = true
      startRefreshInterval()
      void loadFresh(context)
      return
    }

    currentConfiguration.value = {}
    isInitialized.value = true
    startRefreshInterval()

    const shouldBlock = options.blockWhenMissing ?? true
    if (shouldBlock) {
      currentConfiguration.value = await loadFresh(context)
      return
    }

    void loadFresh(context)
  }

  const deinitialize = () => {
    persist()
    stopRefreshInterval()
    currentContext.value = undefined
    currentConfiguration.value = {}
    isInitialized.value = false
    isLoading.value = false
  }

  function get<T = unknown>(key: string): T | undefined
  function get<T>(key: string, defaultValue: T): T
  function get<T = unknown>(key: string, defaultValue?: T): T | undefined {
    const value = getValue(currentConfiguration.value, key)
    return value === undefined ? defaultValue : (value as T)
  }

  async function getFresh<T = unknown>(key: string): Promise<T | undefined>
  async function getFresh<T>(key: string, defaultValue: T): Promise<T>
  async function getFresh<T = unknown>(
    key: string,
    defaultValue?: T,
  ): Promise<T | undefined> {
    if (authStore.isAuthenticated && authStore.currentOrgId) {
      if (!authStore.currentCenterId) return get(key, defaultValue)

      const context: AppConfigurationContext = {
        isAuthenticated: authStore.isAuthenticated,
        orgId: authStore.currentOrgId,
        centerId: authStore.currentCenterId,
      }

      currentConfiguration.value = await loadFresh(context)
    }

    return get(key, defaultValue)
  }

  const loadFresh = async (context: AppConfigurationContext) => {
    const key = getContextKey(context)
    const pendingLoad = pendingLoads.get(key)
    if (pendingLoad) return pendingLoad

    const loadPromise = resolveConfiguration(context)
    pendingLoads.set(key, loadPromise)

    try {
      return await loadPromise
    } finally {
      pendingLoads.delete(key)
    }
  }

  const resolveConfiguration = async (context: AppConfigurationContext) => {
    isLoading.value = true

    try {
      // Can't use the v2Client here because it has to run on init
      const configuration = await request
        .get<Record<string, unknown>>('/config-node/configuration/resolve')
        .then((response) => response.data)

      setStoredEntry(context, {
        configuration,
        resolvedAt: new Date().toISOString(),
      })
      persist()

      if (isCurrentContext(context)) {
        currentConfiguration.value = configuration
      }

      return configuration
    } catch (error) {
      logError('Failed to resolve configuration', error)

      const storedEntry = getStoredEntry(context)
      if (storedEntry) return storedEntry.configuration

      throw error
    } finally {
      isLoading.value = false
    }
  }

  const getValue = (configuration: Record<string, unknown>, path: string) => {
    return path.split('.').reduce<unknown>((value, segment) => {
      if (!isRecord(value)) return undefined
      return value[segment]
    }, configuration)
  }

  const startRefreshInterval = () => {
    if (refreshInterval.value) return

    refreshInterval.value = setInterval(() => {
      if (!authStore.isAuthenticated || !authStore.currentOrgId) return
      if (!authStore.currentCenterId) return

      void loadFresh({
        isAuthenticated: authStore.isAuthenticated,
        orgId: authStore.currentOrgId,
        centerId: authStore.currentCenterId,
      })
    }, REFRESH_INTERVAL_MS)
  }

  const stopRefreshInterval = () => {
    if (!refreshInterval.value) return

    clearInterval(refreshInterval.value)
    refreshInterval.value = undefined
  }

  const getStoredEntry = (context: AppConfigurationContext) => {
    return configurations.value[context.orgId]?.[context.centerId]
  }

  const setStoredEntry = (
    context: AppConfigurationContext,
    entry: AppConfigurationEntry,
  ) => {
    configurations.value = {
      ...configurations.value,
      [context.orgId]: {
        ...configurations.value[context.orgId],
        [context.centerId]: entry,
      },
    }
  }

  const getContextKey = (context: AppConfigurationContext) => {
    return `${context.orgId}:${context.centerId}`
  }

  const isCurrentContext = (context: AppConfigurationContext) => {
    const activeContext = currentContext.value
    return (
      activeContext?.orgId === context.orgId &&
      activeContext?.centerId === context.centerId
    )
  }

  const loadFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      configurations.value = stored ? JSON.parse(stored) : {}
    } catch (error) {
      configurations.value = {}
      logError('Failed to load stored configuration', error)
    }
  }

  const persist = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(configurations.value))
    } catch (error) {
      logError('Failed to persist configuration', error)
    }
  }

  const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
  }

  const logError = (message: string, error: unknown) => {
    if (!process.env.DEV) return
    console.error(`[AppConfigurationStore] ${message}:`, error)
  }

  loadFromStorage()

  watch(
    (): AppConfigurationContext => ({
      isAuthenticated: authStore.isAuthenticated,
      orgId: authStore.currentOrgId ?? '',
      centerId: authStore.currentCenterId ?? '',
    }),
    async (context, previousContext) => {
      if (!context.isAuthenticated || !context.orgId) {
        deinitialize()
        return
      }

      const orgChanged =
        !!previousContext?.orgId && previousContext.orgId !== context.orgId

      await initialize({
        blockWhenMissing: !isInitialized.value || orgChanged,
      })
    },
    { immediate: true },
  )

  return {
    isLoading,
    initialize,
    deinitialize,
    get,
    getFresh,
  }
})
